'use strict';
import isUndefined from 'lodash/isUndefined';
import memoize from 'lodash/memoize';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/observable/combineLatest';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/takeUntil';
import {SCOPE_DESTROY} from '../../config/constants/view-events';
import {epicErrorHandler, observeNewOnScope, eventToObservable} from '../../core-utilities/rx-utils';
import template from './template.html';
import loadingImg from '../../../img/loading.svg';
import noDataImg from '../../../img/no-data.svg';
import noNetworkImg from '../../../img/no-network.svg';
import * as LOADING_STATE from './loading-states';
import {logger} from './logger';


/**
 * Attributes supported:
 * timeout:        override the default timeout (must be a value, not interpolated)
 * loadingLabel:   override the loading label text
 * noDataLabel:    override the no-data label text
 * noNetworkLabel: override the no-network label text
 */
LoadingIconDirective.$inject = [];
export function LoadingIconDirective() {
  return {
    restrict: 'E',
    template: template,
    scope: {
      iconMode: '=',
    },
    controllerAs: 'loadingIcon',
    bindToController: true,
    controller: 'LoadingIconController',
  };
}

/* All network observables are terminated when the loaded condition is met.
 * This component does not currently continue to monitor network status after data is loaded.
 */
LoadingIconController.$inject = [
  '$scope',
  '$attrs',
  '$translate',
  'FirebaseUtil',
];

export function LoadingIconController(
  $scope,
  $attrs,
  $translate,
  FirebaseUtil,
) {
  const vm = this;
  const getLabel = memoize(getLabelFromAttrs);
  vm.iconMode = LOADING_STATE.LOADING;

  $scope.$evalAsync(init);

  function init() {
    const leave$ = eventToObservable($scope, SCOPE_DESTROY);

    const iconMode$ = observeNewOnScope($scope, 'loadingIcon.iconMode'); // requires vm name

    const network$ = FirebaseUtil
      .firebaseRef('.info/connected')
      .observe('value')
      .map(ss => ss.val());

    Observable.combineLatest(
      network$.map(status => status ? null : LOADING_STATE.NO_NETWORK),
      iconMode$,
      (networkMode, mode) => networkMode || mode
    )
      .distinctUntilChanged()
      .switchMap(getMode)
      .catch(epicErrorHandler(logger, 'iconMode$'))
      .takeUntil(leave$)
      .subscribe(changeMode);
  }

  // TODO: change this to directly render the updated img src and label text delayed with requestAnimationFrame to remove any added watches on the scope
  function changeMode(mode) {
    $scope.$evalAsync(function () {
      vm.show = mode.show;
      vm.img = mode.img;
      vm.label = mode.label;
    });
  }

  function getMode(mode) {
    switch (mode) {
      case LOADING_STATE.LOADING:
        return $translate(getLabel(mode)).then(msg => ({
          name: 'LOADING',
          show: true,
          img: loadingImg,
          label: msg,
        }));
      case LOADING_STATE.LOADED:
      case LOADING_STATE.PLACEHOLDER:
        return Promise.resolve({
          name: LOADING_STATE.getValue(mode),
          show: false,
          img: '',
          label: '',
        });
      case LOADING_STATE.NO_DATA:
        return $translate(getLabel(mode)).then(msg => ({
          name: 'NO_DATA',
          show: true,
          img: noDataImg,
          label: msg,
        }));
      case LOADING_STATE.NO_NETWORK:
        return $translate(getLabel(mode)).then(msg => ({
          name: 'NO_NETWORK',
          show: true,
          img: noNetworkImg,
          label: msg,
        }));
      default:
        return Promise.reject(new Error('Invalid LoadingIcon Mode'));
    }
  }

  function getLabelFromAttrs(mode) {
    switch (mode) {
      case LOADING_STATE.LOADING:
        return !isUndefined($attrs.loadingLabel) ? $attrs.loadingLabel : 'default.loading.label';
      case LOADING_STATE.NO_DATA:
        return !isUndefined($attrs.noDataLabel) ? $attrs.noDataLabel : 'default.loading.no.data.label';
      case LOADING_STATE.NO_NETWORK:
        return !isUndefined($attrs.noNetworkLabel) ? $attrs.noNetworkLabel : 'default.loading.no.network.label';
      case LOADING_STATE.LOADED:
      case LOADING_STATE.PLACEHOLDER:
        return '';
      default:
        return new Error('Invalid LoadingIcon Mode');
    }
  }
}



// WEBPACK FOOTER //
// ./src/js/core-components/loading-icon/LoadingIconDirective.js